import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {Profile} from '@/model/network/profile/Profile';

import {INetworkProfileService} from '@/services/network/INetworkProfileService';
import {ConnectionType} from '@/model/network/connection/ConnectionType';

@Module({name: 'NetworkProfilesStore', namespaced: true})
export default class NetworkProfilesStore extends VuexModule {

  private static sort(profiles: Profile[]): void {
    profiles.sort((a, b) => {
      if (!a.Settings?.Connection || !b.Settings?.Connection || !a.Settings?.Connection.Type || !b.Settings?.Connection.Type) {
        return 0;
      }

      const typeWeight = a.Settings?.Connection.Type.localeCompare(b.Settings?.Connection.Type);

      const nameWeight = a.Id.localeCompare(b.Id);

      return typeWeight * 10 + nameWeight;
    });
  }

  private profiles: Profile[] | null = null;

  @Inject(Symbol.for('INetworkProfileService'))
  private profileService!: INetworkProfileService;

  @Action({commit: 'setProfiles', rawError: true})
  public async fetchProfiles(fetchDetails: boolean = false) {
    const profiles = await this.profileService.readAll();

    if (fetchDetails && profiles) {
      for (const profile of profiles) {
        if (profile.Id !== undefined) {
          Object.assign(profile, await this.profileService.read(profile.Id));
        }
      }
    }

    return profiles;
  }

  @Mutation
  public setProfiles(profiles: Profile[]) {
    NetworkProfilesStore.sort(profiles);
    this.profiles = profiles;
  }

  public get Profiles(): Profile[] | null {
    return this.profiles;
  }

  public get ProfilesVpn(): Profile[] | null {
    return this.profiles?.filter((p) => p.Settings?.Connection?.Type === ConnectionType.Vpn) ?? null;
  }

  @Action({rawError: true})
  public async fetchProfilesDetails() {

    if (!this.profiles) {
      await this.fetchProfiles();
    }

    if (!this.profiles) {
      throw new Error('Profile list still null after fetch');
    }

    for (const profile of this.profiles) {
      if (profile.Id !== undefined) {
        await this.fetchProfile(profile.Id);
      }
    }
  }

  @Action({commit: 'setProfile', rawError: true})
  public async fetchProfile(id: string) {
    return await this.profileService.read(id);
  }

  @Mutation
  public setProfile(profile: Profile) {

    if (!this.profiles) {
      this.profiles = [];
    }

    this.profiles = this.profiles.filter((p) => p.Id !== profile.Id);
    this.profiles.push(profile);
    NetworkProfilesStore.sort(this.profiles);
  }

  @Mutation
  public addProfile(profile: Profile) {

    if (!this.profiles) {
      this.profiles = [];
    }

    this.profiles.push(profile);
    NetworkProfilesStore.sort(this.profiles);
  }

  @Mutation
  public removeProfile(profile: Profile) {

    if (!this.profiles) {
      throw new Error('profile could not be removed: no existing profiles');
    }

    const foundProfile = this.profiles.find((p) => p.Id !== profile.Id);
    if (!foundProfile) {
      throw new Error('profile could not be removed: not found');
    }

    this.profiles = this.profiles.filter((p) => p.Id !== profile.Id);
  }

  @Action
  public getProfile(uuid: string): Profile | null {
    return this.profiles?.find((p) => p.Settings?.Connection?.Uuid === uuid) ?? null;
  }
}
